import { useNavigate } from "react-router-dom"

export default function NotFound() {
  const navigation = useNavigate()

  const handleGoHome = () => {
    navigation("/jobs")
  }


  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-cyan-50 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background Decorative Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Large Circle */}
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-indigo-200 to-purple-200 rounded-full opacity-20 blur-3xl"></div>

        {/* Medium Circle */}
        <div className="absolute -bottom-32 -left-32 w-64 h-64 bg-gradient-to-tr from-cyan-200 to-blue-200 rounded-full opacity-20 blur-2xl"></div>

        {/* Small Circles */}
        <div className="absolute top-20 left-20 w-32 h-32 bg-gradient-to-br from-indigo-300 to-purple-300 rounded-full opacity-10 blur-xl"></div>
        <div className="absolute bottom-20 right-20 w-24 h-24 bg-gradient-to-tr from-cyan-300 to-blue-300 rounded-full opacity-10 blur-lg"></div>

        {/* Geometric Shapes */}
        <div className="absolute top-1/4 right-1/4 w-16 h-16 bg-indigo-200 opacity-20 rotate-45 rounded-lg animate-float"></div>
        <div className="absolute bottom-1/3 left-1/4 w-12 h-12 bg-purple-200 opacity-20 rotate-12 rounded-lg animate-float-delayed"></div>

        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 text-center max-w-2xl mx-auto">
        {/* 404 Illustration */}
        <div className="mb-8">
          <div className="relative inline-block">
            {/* Large 404 Text */}
            <div className="text-9xl md:text-[12rem] font-bold text-transparent bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text leading-none">
              404
            </div>

            {/* Floating Resume Icon */}
            <div className="absolute -top-4 -right-4 w-16 h-16 bg-white rounded-lg shadow-lg border-2 border-indigo-200 flex items-center justify-center animate-float">
              <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>

            {/* Floating Briefcase Icon */}
            <div className="absolute -bottom-2 -left-6 w-12 h-12 bg-white rounded-lg shadow-lg border-2 border-purple-200 flex items-center justify-center animate-float-delayed">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0H8m8 0v2a2 2 0 002 2v8a2 2 0 01-2 2H8a2 2 0 01-2-2v-8a2 2 0 012-2V8"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Error Message */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Oops! This Job Has Been Filled</h1>
          <div className="space-y-3 text-lg text-gray-600 max-w-lg mx-auto">
            <p>
              🔍 <span className="font-medium">Job Status:</span> Position Not Found
            </p>
            <p>
              📋 <span className="font-medium">Requirements:</span> Valid URL needed
            </p>
            <p>
              💼 <span className="font-medium">Experience Level:</span> 404 years (too much!)
            </p>
          </div>
        </div>

        {/* Funny Job-Related Messages */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 p-6 mb-8 max-w-md mx-auto">
          <div className="text-center">
            <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Interview Feedback</h3>
            <p className="text-gray-600 text-sm leading-relaxed">
              "Unfortunately, we've decided to go with another candidate...
              <br />
              <span className="italic">Actually, we went with another webpage."</span>
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <button
            onClick={handleGoHome}
            className="group relative inline-flex items-center justify-center px-8 py-3 text-base font-medium text-white bg-indigo-600 border border-transparent rounded-xl hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200 shadow-lg hover:shadow-xl min-w-[200px]"
          >
            <svg
              className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
              />
            </svg>
            Apply for Home Page
          </button>

          {/* <button
            onClick={handleGoHome}
            className="group relative inline-flex items-center justify-center px-8 py-3 text-base font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-xl hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200 min-w-[200px]"
          >
            <svg
              className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Previous Position
          </button> */}
        </div>

        {/* Additional Help */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500 mb-4">Still looking for opportunities? Try these popular searches:</p>
          <div className="flex flex-wrap justify-center gap-2">
            {/* <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
              /dashboard
            </span> */}
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
              /login
            </span>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800">
              /jobs
            </span>
          </div>
        </div>
      </div>

      {/* Floating Elements */}
      <div className="absolute top-10 left-10 w-2 h-2 bg-indigo-400 rounded-full opacity-60 animate-pulse"></div>
      <div className="absolute top-32 right-16 w-1 h-1 bg-purple-400 rounded-full opacity-60 animate-pulse delay-1000"></div>
      <div className="absolute bottom-16 left-16 w-1.5 h-1.5 bg-cyan-400 rounded-full opacity-60 animate-pulse delay-500"></div>
      <div className="absolute bottom-32 right-32 w-1 h-1 bg-blue-400 rounded-full opacity-60 animate-pulse delay-700"></div>

      {/* Additional Decorative Job Icons */}
      <div className="absolute top-1/4 left-10 opacity-10">
        <svg className="w-8 h-8 text-indigo-600 animate-float" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0H8m8 0v2a2 2 0 002 2v8a2 2 0 01-2 2H8a2 2 0 01-2-2v-8a2 2 0 012-2V8"
          />
        </svg>
      </div>

      <div className="absolute bottom-1/4 right-10 opacity-10">
        <svg
          className="w-6 h-6 text-purple-600 animate-float-delayed"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
      </div>
    </div>
  )
}
